import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { TopMatchedApplicant } from "../../../../api/types/job.types";

import { useUserDataFormatter } from "../../../../../User/hooks/useUserDataFormatter";

import { useGetTopMatchedApplicants } from "../../../../api/queries/useGetTopMatchedApplicants";

export const TopApplicants = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.overViewTab" });
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const handleViewAllCandidates = () => {
    navigate({ search: "?tab=candidates" });
  };

  const { data, isLoading } = useGetTopMatchedApplicants({ jobId });

  const applicantsData: TopMatchedApplicant[] = data || [];

  const tableColumns = [
    { label: t("applicants"), value: "applicants" },
    { label: t("fitScoreByGINO"), value: "fitScoreByGINO" },
  ];

  if (!isLoading && applicantsData?.length === 0) return <></>;

  return (
    <>
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerContainerInner}>
          <Typography sx={styles.sectionTitle}>{t("top5BestMatchedApplicants")}</Typography>
          <Button variant="text-md" sx={styles.seeAllButton} onClick={handleViewAllCandidates}>
            {t("seeAll")}
          </Button>
        </Box>
      </Box>
      <Table sx={styles.tableContainer}>
        <TableHead>
          <TableRow>
            {tableColumns.map(({ label, value }) => (
              <TableCell key={value} sx={styles.tableCell(value === "fitScoreByGINO")}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, idx) => <TopApplicantsSkeletonRow key={idx} />)
            : applicantsData?.map((row, idx) => <ApplicantRow key={idx} applicant={row} />)}
        </TableBody>
      </Table>
    </>
  );
};

const TopApplicantsSkeletonRow = () => {
  return (
    <TableRow>
      <TableCell align="left">
        <Box sx={styles.applicantInfoContainer}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={styles.applicantInfoContainerInner}>
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={150} height={18} />
            <Skeleton variant="text" width={100} height={18} />
          </Box>
        </Box>
      </TableCell>
      <TableCell align="left">
        <Box sx={styles.applicantFitScoreContainer}>
          <Skeleton variant="rounded" sx={{ flex: 1 }} height={8} />
          <Skeleton variant="text" width={30} height={20} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

const ApplicantRow = ({ applicant }: { applicant: TopMatchedApplicant }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "overviewPage" });

  const { fullName, firstTwoChr, role, club, countryName, cityName, media, mediaFileName, profileLink } =
    useUserDataFormatter(applicant as any);

  const ginoScore = applicant.fitScore.percent;
  return (
    <TableRow>
      <TableCell align="left">
        <Box sx={styles.applicantInfoContainer}>
          <Avatar sx={styles.applicantAvatar} src={media} alt={mediaFileName} component={Link} to={profileLink}>
            {firstTwoChr}
          </Avatar>
          <Box sx={styles.applicantInfoContainerInner} component={Link} to={profileLink}>
            <Typography sx={styles.applicantName}>{fullName}</Typography>
            <Typography sx={styles.applicantRoleAndClub}>{`${role} ${club ? `• ${club}` : ""}`}</Typography>
            <Typography sx={styles.applicantCountryAndCity}>
              {`${countryName} ${cityName ? `• ${cityName}` : ""}`}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="left">
        <Box sx={styles.applicantFitScoreContainer}>
          <LinearProgress variant="determinate" value={ginoScore} sx={styles.applicantFitScoreProgress} />
          <Typography sx={styles.applicantFitScorePercentage}>{ginoScore}%</Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const styles = {
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerContainerInner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mx: "24px",
  },
  tableCell: (isFitScoreByGINO: boolean) => ({
    width: { xs: "110px", sm: isFitScoreByGINO ? "200px" : "auto" },
    padding: { xs: "12px 16px", sm: "16px 24px" },
  }),
  sectionTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  seeAllButton: {
    textWrap: "nowrap",
  },
  tableContainer: {
    "& .MuiTableHead-root": {
      backgroundColor: "var(--Gray-50, #F9FAFB)",
    },
    "& .MuiTableCell-head": {
      borderTop: "1px solid var(--Gray-200, #EAECF0)",
      color: "var(--Gray-600, #475467)",
      fontFamily: "Montserrat",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      padding: { xs: "12px 16px", sm: "12px 24px" },
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      padding: "16px 24px",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  applicantInfoContainer: { display: "flex", justifyContent: "flex-start", gap: "12px" },
  applicantInfoContainerInner: { display: "flex", flexDirection: "column", gap: "4px", textDecoration: "none" },
  applicantAvatar: { height: "48px", width: "48px" },
  applicantName: {
    color: "var(--grey-100, #171A1C)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    textDecoration: "none",
  },
  applicantRoleAndClub: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  },
  applicantCountryAndCity: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
  applicantFitScoreContainer: { display: "flex", gap: "12px", alignItems: "center" },
  applicantFitScoreProgress: { flex: 1, height: "8px", borderRadius: "4px" },
  applicantFitScorePercentage: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
